// src/components/admin/StoreManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ProductsTab, OrdersTab, ProductModal } from './StoreManagementComponents';

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23', // Olive Green
  secondary: '#2C5234',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40'
};

const StoreManagement = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Product modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    short_description: '',
    brand: '',
    manufacturer: '',
    category_id: '',
    price: '',
    compare_price: '',
    member_price: '',
    stock_quantity: 0,
    low_stock_threshold: 5,
    weight: '',
    status: 'draft',
    is_featured: false,
    is_digital: false,
    product_condition: 'new',
    main_image: ''
  });

  // Image upload states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Search and filter states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  if (!API_BASE || !BACKEND_URL) {
    console.error('❌ CRITICAL: API URLs not configured in StoreManagement!');
  }

  useEffect(() => {
    fetchData();
  }, [activeTab, filterCategory, filterStatus]);

  const fetchData = async () => {
    try {
      if (activeTab === 'products') {
        await Promise.all([fetchProducts(), fetchCategories(), fetchStats()]);
      } else if (activeTab === 'orders') {
        await fetchOrders();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      let url = `${API_BASE}/admin/store/products?limit=100`;

      if (productSearchTerm) url += `&search=${encodeURIComponent(productSearchTerm)}`;
      if (filterCategory) url += `&category=${filterCategory}`;
      if (filterStatus) url += `&status=${filterStatus}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setProducts(data.data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/store/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/store/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      let url = `${API_BASE}/admin/store/orders?limit=100`;

      if (orderSearchTerm) url += `&search=${encodeURIComponent(orderSearchTerm)}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setOrders(data.data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload a valid image (JPG, PNG, GIF, WebP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 5MB');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return productForm.main_image;

    try {
      setUploadingImage(true);
      const token = localStorage.getItem('adminToken');
      const uploadFormData = new FormData();
      uploadFormData.append('product_image', imageFile);

      const response = await fetch(`${API_BASE}/admin-upload/store/product-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Image uploaded successfully');
        return data.data.imagePath;
      } else {
        toast.error(data.message || 'Failed to upload image');
        return productForm.main_image;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      return productForm.main_image;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload image first if selected
      const imagePath = await uploadImage();

      const dataToSubmit = {
        ...productForm,
        main_image: imagePath,
        price: parseFloat(productForm.price) || 0,
        compare_price: productForm.compare_price ? parseFloat(productForm.compare_price) : null,
        member_price: productForm.member_price ? parseFloat(productForm.member_price) : null,
        stock_quantity: parseInt(productForm.stock_quantity) || 0,
        low_stock_threshold: parseInt(productForm.low_stock_threshold) || 5,
        weight: productForm.weight ? parseFloat(productForm.weight) : null,
        category_id: productForm.category_id || null
      };

      const token = localStorage.getItem('adminToken');
      const url = editingProduct
        ? `${API_BASE}/admin/store/products/${editingProduct.id}`
        : `${API_BASE}/admin/store/products`;

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      const data = await response.json();

      if (data.success) {
        setShowProductModal(false);
        resetProductForm();
        fetchProducts();
        fetchStats();
        toast.success(`Product ${editingProduct ? 'updated' : 'created'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      sku: product.sku || '',
      description: product.description || '',
      short_description: product.short_description || '',
      brand: product.brand || '',
      manufacturer: product.manufacturer || '',
      category_id: product.category_id || '',
      price: product.price || '',
      compare_price: product.compare_price || '',
      member_price: product.member_price || '',
      stock_quantity: product.stock_quantity || 0,
      low_stock_threshold: product.low_stock_threshold || 5,
      weight: product.weight || '',
      status: product.status || 'draft',
      is_featured: product.is_featured === 1,
      is_digital: product.is_digital === 1,
      product_condition: product.product_condition || 'new',
      main_image: product.main_image || ''
    });
    setImagePreview(product.main_image ? `${BACKEND_URL}${product.main_image}` : null);
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/store/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        fetchProducts();
        fetchStats();
        toast.success('Product deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/store/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (data.success) {
        fetchOrders();
        toast.success('Order status updated successfully');
      } else {
        toast.error(data.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      sku: '',
      description: '',
      short_description: '',
      brand: '',
      manufacturer: '',
      category_id: '',
      price: '',
      compare_price: '',
      member_price: '',
      stock_quantity: 0,
      low_stock_threshold: 5,
      weight: '',
      status: 'draft',
      is_featured: false,
      is_digital: false,
      product_condition: 'new',
      main_image: ''
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'bg-success';
      case 'draft': return 'bg-secondary';
      case 'inactive': return 'bg-warning text-dark';
      case 'discontinued': return 'bg-danger';
      case 'out_of_stock': return 'bg-dark';
      case 'pending': return 'bg-warning text-dark';
      case 'confirmed': return 'bg-info text-white';
      case 'processing': return 'bg-primary';
      case 'shipped': return 'bg-primary';
      case 'delivered': return 'bg-success';
      case 'cancelled': return 'bg-danger';
      case 'refunded': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !products.length && !orders.length) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: ARCA_COLORS.primary }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-3">Loading store data...</span>
      </div>
    );
  }

  console.log('StoreManagement Render:', { activeTab, productsCount: products.length, ordersCount: orders.length, stats });
  console.log('Products:', products);
  console.log('Categories:', categories);

  return (
    <div className="container-fluid px-4 py-3" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: ARCA_COLORS.primary }}>
            Store Management
          </h2>
          <p className="text-muted mb-0">Manage products, inventory, and orders</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && activeTab === 'products' && (
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${ARCA_COLORS.primary}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Total Products</p>
                    <h3 className="mb-0 fw-bold">{stats.total_products || 0}</h3>
                  </div>
                  <div className="fs-1" style={{ color: ARCA_COLORS.primary, opacity: 0.3 }}>
                    <i className="bi bi-box-seam"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${ARCA_COLORS.success}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Active</p>
                    <h3 className="mb-0 fw-bold">{stats.active_products || 0}</h3>
                  </div>
                  <div className="fs-1 text-success" style={{ opacity: 0.3 }}>
                    <i className="bi bi-check-circle"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${ARCA_COLORS.warning}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Low Stock</p>
                    <h3 className="mb-0 fw-bold">{stats.low_stock_products || 0}</h3>
                  </div>
                  <div className="fs-1 text-warning" style={{ opacity: 0.3 }}>
                    <i className="bi bi-exclamation-triangle"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${ARCA_COLORS.info}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Total Inventory</p>
                    <h3 className="mb-0 fw-bold">{stats.total_inventory || 0}</h3>
                  </div>
                  <div className="fs-1 text-info" style={{ opacity: 0.3 }}>
                    <i className="bi bi-boxes"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4" role="tablist" style={{ borderBottom: `2px solid ${ARCA_COLORS.primary}`, display: 'flex' }}>
        <li className="nav-item" style={{ display: 'inline-block' }}>
          <button
            type="button"
            className={`nav-link d-flex align-items-center ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => {
              console.log('Switching to products tab');
              setActiveTab('products');
            }}
            style={{
              color: activeTab === 'products' ? 'white' : ARCA_COLORS.dark,
              backgroundColor: activeTab === 'products' ? ARCA_COLORS.primary : 'transparent',
              fontWeight: activeTab === 'products' ? 'bold' : '500',
              border: `2px solid ${activeTab === 'products' ? ARCA_COLORS.primary : '#dee2e6'}`,
              borderBottom: activeTab === 'products' ? 'none' : '2px solid #dee2e6',
              cursor: 'pointer',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              marginRight: '0.5rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              transition: 'all 0.3s ease'
            }}
          >
            <i className="bi bi-box-seam me-2" style={{ fontSize: '1.2rem' }}></i>
            <span style={{ display: 'inline-block' }}>Products</span>
          </button>
        </li>
        <li className="nav-item" style={{ display: 'inline-block' }}>
          <button
            type="button"
            className={`nav-link d-flex align-items-center ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => {
              console.log('Switching to orders tab');
              setActiveTab('orders');
            }}
            style={{
              color: activeTab === 'orders' ? 'white' : ARCA_COLORS.dark,
              backgroundColor: activeTab === 'orders' ? ARCA_COLORS.primary : 'transparent',
              fontWeight: activeTab === 'orders' ? 'bold' : '500',
              border: `2px solid ${activeTab === 'orders' ? ARCA_COLORS.primary : '#dee2e6'}`,
              borderBottom: activeTab === 'orders' ? 'none' : '2px solid #dee2e6',
              cursor: 'pointer',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className="bi bi-cart me-2" style={{ fontSize: '1.2rem', display: 'inline-block' }}></i>
            <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>Orders</span>
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content" style={{ minHeight: '400px', display: 'block' }}>
        {console.log('Rendering tab content, activeTab:', activeTab)}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <ProductsTab
            products={products}
            categories={categories}
            productSearchTerm={productSearchTerm}
            setProductSearchTerm={setProductSearchTerm}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            handleEditProduct={handleEditProduct}
            handleDeleteProduct={handleDeleteProduct}
            setShowProductModal={setShowProductModal}
            resetProductForm={resetProductForm}
            getStatusBadgeClass={getStatusBadgeClass}
            formatCurrency={formatCurrency}
            API_BASE={API_BASE}
            ARCA_COLORS={ARCA_COLORS}
          />
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <OrdersTab
            orders={orders}
            orderSearchTerm={orderSearchTerm}
            setOrderSearchTerm={setOrderSearchTerm}
            handleUpdateOrderStatus={handleUpdateOrderStatus}
            getStatusBadgeClass={getStatusBadgeClass}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            ARCA_COLORS={ARCA_COLORS}
          />
        )}

      </div>
      {/* End Tab Content */}

      {/* Product Modal - Continues in separate component */}
      {showProductModal && (
        <ProductModal
          show={showProductModal}
          onHide={() => {
            setShowProductModal(false);
            resetProductForm();
          }}
          editingProduct={editingProduct}
          productForm={productForm}
          setProductForm={setProductForm}
          categories={categories}
          imagePreview={imagePreview}
          handleImageChange={handleImageChange}
          uploadingImage={uploadingImage}
          handleProductSubmit={handleProductSubmit}
          ARCA_COLORS={ARCA_COLORS}
        />
      )}
    </div>
  );
};

// Separate components for tabs will be in additional files
export default StoreManagement;
